// app/javascript/controllers/dae20/filter_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    baseUrl: String,
    autoApply: { type: Boolean, default: false },
    applyDelay: { type: Number, default: 500 }
  }

  connect() {
    this.restoreFormValues()
    if (this.autoApplyValue) {
      this._setupAutoApply()
    }
  }

  disconnect() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
    }
  }

  clearForm() {
    try {
      const form = this.element.querySelector("form")
      if (!form) return

      form.reset()
      form.querySelectorAll("input, select, textarea").forEach((input) => {
        if (input.type === "checkbox" || input.type === "radio") {
          input.checked = false
        } else {
          input.value = ""
        }
      })
    } catch (error) {
      console.error("Error clearing form:", error)
    }
  }

  // Сброс к значениям по умолчанию (data-default-value)
  resetToDefaults() {
    try {
      const form = this.element.querySelector("form")
      if (!form) return

      form.querySelectorAll("input, select, textarea").forEach((el) => {
        const defaultValue = el.dataset.defaultValue
        if (defaultValue !== undefined && defaultValue !== null && defaultValue !== "") {
          el.value = defaultValue
        } else {
          if (el.type === "checkbox" || el.type === "radio") {
            el.checked = false
          } else {
            el.value = ""
          }
        }
      })

      // Применить фильтры с дефолтными значениями
      this._submitFilter()
    } catch (error) {
      console.error("Error resetting to defaults:", error)
    }
  }

  restoreFormValues() {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const form = this.element.querySelector("form")
      if (!form) return

      const elements = form.querySelectorAll("input[name], select[name], textarea[name]")
      elements.forEach((el) => {
        const name = el.name
        if (!name) return

        if (el.type === "checkbox" || el.type === "radio") {
          const values = urlParams.getAll(name)
          if (values.includes(el.value)) el.checked = true
          return
        }

        let rawValue = null

        if (name.endsWith("_like") || name.endsWith("_mask")) {
          const baseName = name.replace(/_(like|mask)$/, "")
          rawValue = urlParams.get(`${baseName}[like]`)

          if (rawValue !== null && name.endsWith("_like")) {
            if (rawValue.startsWith("%") && rawValue.endsWith("%") && rawValue.length >= 2) {
              rawValue = rawValue.substring(1, rawValue.length - 1)
            }
          }
        } else if (name.endsWith("_in[]")) {
          // Multi-select / button_group: значения в формате field[in][]=val1&field[in][]=val2
          const baseName = name.replace(/_in\[\]$/, "")
          const values = urlParams.getAll(`${baseName}[in][]`)
          if (values.length > 0) {
            if (el.tagName === "SELECT" && el.multiple) {
              // Multi-select: выбрать все подходящие option
              Array.from(el.options).forEach(opt => {
                opt.selected = values.includes(opt.value)
              })
            } else if (el.type === "hidden") {
              // Button group hidden input: comma-separated
              rawValue = values.join(",")
            }
          }
          if (rawValue === null) return
        } else {
          rawValue = urlParams.get(name)
        }

        if (rawValue === null || rawValue === "") {
          const defaultValue = el.dataset.defaultValue
          if (defaultValue !== undefined && defaultValue !== null && defaultValue !== "") {
            rawValue = defaultValue
          }
        }

        if (rawValue !== null && rawValue !== "") {
          el.value = rawValue
        }
      })

      // Диспатчим событие для button_group контроллеров, чтобы они обновили visual state
      this.element.dispatchEvent(new CustomEvent("filter:restored", { bubbles: true }))
    } catch (error) {
      console.error("Error restoring form values:", error)
    }
  }

  removeComponentParams(params, form) {
    const formFieldNames = Array.from(form.querySelectorAll("input[name], select[name], textarea[name]"))
      .map((el) => el.name)
      .filter((name) => name && name.trim() !== "")

    formFieldNames.forEach((fieldName) => {
      if (fieldName.endsWith("_like")) {
        const baseKey = fieldName.replace(/_like$/, "")
        params.delete(`${baseKey}[like]`)
      } else if (fieldName.endsWith("_mask")) {
        const baseKey = fieldName.replace(/_mask$/, "")
        params.delete(`${baseKey}[like]`)
      } else if (fieldName.endsWith("_in[]")) {
        const baseKey = fieldName.replace(/_in\[\]$/, "")
        // Удаляем все in[] параметры
        const keysToDelete = []
        for (const [key] of params) {
          if (key === `${baseKey}[in][]`) keysToDelete.push(key)
        }
        keysToDelete.forEach(k => params.delete(k))
      } else {
        params.delete(fieldName)
      }
    })

    formFieldNames.forEach((fieldName) => {
      if (!fieldName.endsWith("_like") && !fieldName.endsWith("_mask") && !fieldName.endsWith("_in[]")) {
        params.delete(`${fieldName}_gte`)
        params.delete(`${fieldName}_lte`)
      }
    })
  }

  // Удаление конкретного фильтра (для pills)
  removeFilter(event) {
    try {
      const paramKey = event.currentTarget.dataset.filterParam
      if (!paramKey) return

      const currentUrl = new URL(window.location.href)
      const params = new URLSearchParams(currentUrl.search)
      params.delete(paramKey)
      currentUrl.search = params.toString()
      window.location.href = currentUrl.toString()
    } catch (error) {
      console.error("Error removing filter:", error)
    }
  }

  applyFilter(event) {
    if (event && event.preventDefault) {
      event.preventDefault()
    }
    this._submitFilter()
  }

  // === Private ===

  _submitFilter() {
    const form = this.element.querySelector("form")
    if (!form || !(form instanceof HTMLFormElement)) {
      console.error("Form element not found or invalid")
      return
    }

    try {
      const currentUrl = new URL(window.location.href)
      const targetUrl = this.baseUrlValue
        ? new URL(this.baseUrlValue, window.location.origin)
        : new URL(currentUrl.pathname, window.location.origin)

      const initialSearch = targetUrl.search || currentUrl.search
      const params = new URLSearchParams(initialSearch)

      this.removeComponentParams(params, form)

      const formData = new FormData(form)
      for (const [key, rawValue] of formData.entries()) {
        const value = String(rawValue ?? "").trim()
        if (value === "") continue

        if (key.endsWith("_like")) {
          const baseKey = key.replace(/_like$/, "")
          params.set(`${baseKey}[like]`, `%${value}%`)
        } else if (key.endsWith("_mask")) {
          const baseKey = key.replace(/_mask$/, "")
          params.set(`${baseKey}[like]`, value)
        } else if (key.endsWith("_in[]")) {
          // Button group / multi-select: comma-separated → multiple params
          const baseKey = key.replace(/_in\[\]$/, "")
          const values = value.split(",").filter(v => v !== "")
          if (values.length > 0) {
            values.forEach(v => params.append(`${baseKey}[in][]`, v))
          }
        } else {
          params.set(key, value)
        }
      }

      targetUrl.search = params.toString()
      window.location.href = targetUrl.toString()
    } catch (error) {
      console.error("Error applying filter:", error)
    }
  }

  _setupAutoApply() {
    const form = this.element.querySelector("form")
    if (!form) return

    this._debounceTimer = null

    // Immediate apply для select, date, hidden (button_group)
    form.querySelectorAll("select").forEach(el => {
      el.addEventListener("change", () => this._submitFilter())
    })

    form.querySelectorAll("input[type='date']").forEach(el => {
      el.addEventListener("change", () => this._submitFilter())
    })

    // Слушаем change на hidden input (button_group диспатчит change)
    form.querySelectorAll("input[type='hidden']").forEach(el => {
      el.addEventListener("change", () => this._submitFilter())
    })

    // Debounced apply для текстовых и числовых полей
    const debouncedSubmit = () => {
      clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(() => {
        this._submitFilter()
      }, this.applyDelayValue)
    }

    form.querySelectorAll("input[type='text'], input[type='number']").forEach(el => {
      el.addEventListener("input", debouncedSubmit)
    })
  }
}
